import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Icons — a closed vocabulary over `lucide-react`, not the library itself.
 *
 * Neither app imports from `lucide-react` directly. Both import from here, for the same reason
 * neither app holds its own `Button`: an icon is chrome the design system owns, and a second import
 * path for the same visual language is how a shell ends up with one screen's "edit" pencil looking
 * different from another's. `frontend-source-policy.ts`-style drift is exactly what a closed
 * re-export prevents — a caller can only reach an icon this file names, so the set the two apps draw
 * from is always this list.
 *
 * ## Why a named list instead of `export *`
 *
 * `lucide-react` ships over a thousand icons. Re-exporting all of them would let any call site reach
 * for whichever one seems closest, and a design system with a thousand equally-valid choices has no
 * vocabulary at all — it is the shadcn-drift problem again, one icon at a time instead of one
 * primitive at a time. Every name below is chosen for a real caller in the shell or a screen; a
 * fifteenth icon means a fifteenth line here, each with a one-line note on what it is for, not a
 * wildcard that quietly makes every icon in the library "available".
 *
 * ## Why the size and stroke are fixed here rather than per call site
 *
 * `lucide-react` exports each icon as a component accepting `size` and `strokeWidth` props with the
 * library's own defaults (24, 2). Wrapping every icon in `Icon` fixes both to this design system's
 * numbers — `1.75` because Lucide's icons are drawn on a 24×24 grid at that weight to read cleanly
 * next to Inter's x-height at the sizes this UI actually uses, and a caller-supplied `className`
 * still controls the rendered size through `h-*`/`w-*` utilities rather than through the prop, so an
 * icon composes with `cn()` the same way every other visual primitive here does.
 *
 * ## Decorative by default
 *
 * `aria-hidden` defaults to `true` and `focusable` is always `"false"`. An icon beside a text label —
 * which is every icon in this system; there is no icon-only control anywhere in either app — carries
 * no meaning a screen reader needs from the glyph itself. A caller with a genuine icon-only control
 * still owns its own accessible name on the *button*, the same way `DialogContent`'s close control
 * does; the icon inside it stays decorative either way.
 */
import { AlertTriangle, Archive, BadgeCheck, Building2, CircleHelp, Clock, ExternalLink, Eye, FileText, Handshake, Home, Image as ImageIcon, Inbox, KeyRound, Layers, LogOut, Megaphone, MessageSquare, Moon, Newspaper, PanelLeftClose, PanelLeftOpen, Pencil, Plus, RefreshCw, Search, Shield, Sun, Trash2, UserRound, Users, } from 'lucide-react';
import { cn } from './lib/cn.js';
const strokeWidth = 1.75;
const defaultSize = 20;
/** Wraps one Lucide icon with this system's fixed weight, default size, and decorative default. */
const icon = (Source) => {
    const Wrapped = ({ size = defaultSize, className, 'aria-hidden': ariaHidden = true, ...rest }) => (_jsx(Source, { size: size, strokeWidth: strokeWidth, focusable: "false", "aria-hidden": ariaHidden, className: cn('shrink-0', className), ...rest }));
    return Wrapped;
};
/**
 * The closed set, one entry per real caller.
 *
 * Grouped by where each is used first; several serve more than one screen and are not duplicated.
 */
export const icons = {
    // Shell navigation.
    /** The dashboard home link. */
    home: icon(Home),
    /** The Support nav item, and the queue's page heading. */
    inbox: icon(Inbox),
    /** The Content nav item — six catalog-driven types under one section. */
    layers: icon(Layers),
    /** The Locations nav item — a physical betting location. */
    building: icon(Building2),
    /** The YubiKeys nav item — the fleet-wide hardware pool. */
    key: icon(KeyRound),
    /** Collapse the sidebar to icon-only. */
    sidebarCollapse: icon(PanelLeftClose),
    /** Expand a collapsed sidebar. */
    sidebarExpand: icon(PanelLeftOpen),
    /** Sign out of the session. */
    logOut: icon(LogOut),
    /** The signed-in staff member's role chip. */
    shield: icon(Shield),
    // Theme.
    /** The toggle's target state is light — shown while the dashboard is dark. */
    sun: icon(Sun),
    /** The toggle's target state is dark — shown while the dashboard is light. */
    moon: icon(Moon),
    // Content types, one icon per catalog entry (`dashboardContentCatalog`).
    /** Ofertas. */
    megaphone: icon(Megaphone),
    /** Noticias. */
    newspaper: icon(Newspaper),
    /** FAQs. */
    helpCircle: icon(CircleHelp),
    /** Banners. */
    fileText: icon(FileText),
    /** Hero slides. */
    image: icon(ImageIcon),
    /** Partners. */
    handshake: icon(Handshake),
    // Content and support actions.
    /** Create a new row of any content type. */
    plus: icon(Plus),
    /** Edit an existing row. */
    pencil: icon(Pencil),
    /** Preview a row as a visitor would see it. */
    eye: icon(Eye),
    /** Archive or permanently delete a row. */
    trash: icon(Trash2),
    /** A row already archived. */
    archive: icon(Archive),
    /** A published/live state, and the registration-candidate identity notice. */
    badgeCheck: icon(BadgeCheck),
    // Support workflow.
    /** The queue's search field. */
    search: icon(Search),
    /** A support request's customer message. */
    messageSquare: icon(MessageSquare),
    /** An assignee, and the "assigned to me" filter. */
    userRound: icon(UserRound),
    /** The role matrix and staff generally, where a request is not about one person. */
    users: icon(Users),
    /** A timestamp-bearing row: created, updated, resolved, or a retention window. */
    clock: icon(Clock),
    /** A stale or interrupted side effect, and a destructive confirmation. */
    alertTriangle: icon(AlertTriangle),
    /** Retry a failed reconciliation, or refresh a stat. */
    refresh: icon(RefreshCw),
    /** A record's canonical/legacy address, opened in a new context. */
    externalLink: icon(ExternalLink),
};
//# sourceMappingURL=icons.js.map